import { useMemo, useEffect, memo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getJoinedCommunitiesAction, getNotJoinedCommunitiesAction } from "../../redux/actions/communityActions";
import { getPublicUsersAction, getPublicUsersWithMatchingSkillsAction, getFollowingUsersAction, getFollowersUsersAction } from "../../redux/actions/userActions";
import { getReceivedProfileViewRequestsAction, getSentProfileViewRequestsAction } from "../../redux/actions/profileViewRequestActions";
import { getReceivedFollowRequestsAction } from "../../redux/actions/followRequestActions";
import { 
  FaHome, 
  FaBookmark, 
  FaUsers, 
  FaUsersCog,
  FaLock,
  FaEnvelope,
} from "react-icons/fa";
import { FaBook, FaCompass } from "react-icons/fa";
import { IoMdPeople } from "react-icons/io";
import { useLocation } from "react-router-dom";
import placeholder from "../../assets/placeholder.png";

const ContentWithCards = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  
  const user = useSelector((state) => state.auth?.userData);
  const joinedCommunities = useSelector((state) => state.community?.joinedCommunities);
  const notJoinedCommunities = useSelector((state) => state.community?.notJoinedCommunities);
  const recommendedUsers = useSelector((state) => state.user?.publicUsers);
  const publicUserProfile = useSelector((state) => state.user?.publicUserProfile || {});
  
  const [notJoinedCommunitiesFetched, setNotJoinedCommunitiesFetched] = useState(false);
  const [publicUsersFetched, setPublicUsersFetched] = useState(false);
  const [isCommunitiesExpanded, setIsCommunitiesExpanded] = useState(false);
  const lastFetchedPathnameRef = useRef(null);

  const currentLocation = location.pathname;
  const isSkillsCirclePage = currentLocation.startsWith('/skills-circle');
  const isMyCommunitiesPage = currentLocation === '/my-communities' || 
                               currentLocation.startsWith('/skills-feed/') ||
                               currentLocation.startsWith('/skills-communities/');
  // Treat both own profile (/profile) and public profiles (/profile/username) as profile pages
  const isProfilePage = currentLocation === "/profile" || currentLocation.startsWith("/profile/");
  // Check if viewing a private profile with access denied (no mystId or accessDenied flag)
  const isPrivateProfileAccessDenied = isProfilePage && 
    currentLocation.startsWith("/profile/") && 
    currentLocation !== "/profile" &&
    publicUserProfile?._id && 
    (publicUserProfile?.accessDenied === true || !publicUserProfile?.mystId);

  useEffect(() => {
    // Only fetch authenticated data if user is logged in
    const isAuthenticated = !!user?._id;
    
    if (isAuthenticated) {
      dispatch(getJoinedCommunitiesAction());
      
      const fetchData = async () => {
        await dispatch(getNotJoinedCommunitiesAction());
        setNotJoinedCommunitiesFetched(true);
        
        // Use matching skills endpoint for Skills Circle pages, My Communities pages, and Profile page
        if (isSkillsCirclePage || isMyCommunitiesPage || isProfilePage) {
          await dispatch(getPublicUsersWithMatchingSkillsAction());
        } else {
          await dispatch(getPublicUsersAction());
        }
      };

      fetchData().then(() => {
        setPublicUsersFetched(true);
        lastFetchedPathnameRef.current = location.pathname;
      });

      // Fetch following and followers users when on any skills-circle page
      if (isSkillsCirclePage) {
        dispatch(getFollowingUsersAction(25, 0));
        dispatch(getFollowersUsersAction(25, 0));
        // Fetch profile view request counts (limit=1 just to get the total count)
        dispatch(getReceivedProfileViewRequestsAction(null, 1, 0));
        dispatch(getSentProfileViewRequestsAction(null, 1, 0));
        // Fetch follow request counts (limit=1 just to get the total count)
        dispatch(getReceivedFollowRequestsAction(null, 1, 0));
      }
    } else {
      // Mark as fetched even when not authenticated to prevent loading states
      setNotJoinedCommunitiesFetched(true);
      setPublicUsersFetched(true);
    }
  }, [dispatch, location.pathname, isSkillsCirclePage, isMyCommunitiesPage, isProfilePage, user?._id]);

  const visibleCommunities = useMemo(() => {
    return joinedCommunities?.slice(0, 8);
  }, [joinedCommunities]);

  const [visibleNotJoinedCommunities, remainingCount] = useMemo(() => {
    const visible = notJoinedCommunities?.slice(0, 5) || [];
    const remaining = Math.max((notJoinedCommunities?.length || 0) - 5, 0);
    return [visible, remaining];
  }, [notJoinedCommunities]);

  const communityLinks = useMemo(() => {
    return visibleCommunities?.map((community) => ({
      href: `/skills-feed/${community.name}`,
      label: community.name,
    }));
  }, [visibleCommunities]);

  const isMySkillsCommunitiesPage = currentLocation.startsWith('/skills-communities');
  
  // Hide left sidebar on profile pages and user detail pages
  const isUserDetailPage = currentLocation.startsWith("/user/");
  const isPostDetailPage = currentLocation.startsWith("/post/") || currentLocation.startsWith("/my/post/");

  return (
    <div className={`flex flex-col lg:flex-row gap-2 md:gap-3 max-w-7xl mx-auto ${isPostDetailPage ? 'px-2 md:px-2 lg:pl-2 lg:pr-6' : isProfilePage ? 'px-2 md:px-4 lg:px-6 lg:pl-0' : 'px-2 md:px-4 lg:px-6 lg:pl-2'}`}>
      {/* Left Cards Sidebar - Hide on profile pages, user detail pages, post detail pages, and mobile */}
      {!isProfilePage && !isUserDetailPage && !isPostDetailPage && (
        <div className="hidden md:block lg:w-60 space-y-2 md:space-y-3 order-2 lg:order-1 lg:-ml-2">
            {/* Navigation Card */}
            {!isSkillsCirclePage && !isMySkillsCommunitiesPage && (
              <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm">
                <div className="flex flex-col space-y-0">
                  <Link
                    className={`flex gap-2 md:gap-3 items-center text-sm font-medium py-2 md:py-2.5 px-2 md:px-3 rounded-lg transition-all duration-200 ${
                      currentLocation === "/skills-feed" || currentLocation === "/"
                        ? "text-primary-700 bg-primary-50"
                        : "text-gray-700 hover:text-primary-800 hover:bg-gray-50"
                    }`}
                    to="/skills-feed"
                  >
                    <FaHome className={`text-base md:text-lg transition-colors ${
                      currentLocation === "/skills-feed" || currentLocation === "/"
                        ? "text-primary-700"
                        : "text-gray-500"
                    }`} />
                    <span className="font-roboto-flex text-sm md:text-base">Skills Feed</span>
                  </Link>
                  
                  {user && user.role === "general" && (
                    <Link
                      className={`flex gap-2 md:gap-3 items-center text-sm font-medium py-2 md:py-2.5 px-2 md:px-3 rounded-lg transition-all duration-200 ${
                        currentLocation === "/skills-feed/following"
                          ? "text-primary-700 bg-primary-50"
                          : "text-gray-700 hover:text-primary-800 hover:bg-gray-50"
                      }`}
                      to="/skills-feed/following"
                    >
                      <FaUsers className={`text-base md:text-lg transition-colors ${
                        currentLocation === "/skills-feed/following"
                          ? "text-primary-700"
                          : "text-gray-500"
                      }`} />
                      <span className="font-roboto-flex text-sm md:text-base">Posts from Following</span>
                    </Link>
                  )}

                  <Link
                    className={`flex gap-2 md:gap-3 items-center text-sm font-medium py-2 md:py-2.5 px-2 md:px-3 rounded-lg transition-all duration-200 ${
                      currentLocation === "/skills-feed/saved"
                        ? "text-primary-700 bg-primary-50"
                        : "text-gray-700 hover:text-primary-800 hover:bg-gray-50"
                    }`}
                    to="/skills-feed/saved"
                  >
                    <FaBookmark className={`text-base md:text-lg transition-colors ${
                      currentLocation === "/skills-feed/saved"
                        ? "text-primary-700"
                        : "text-gray-500"
                    }`} />
                    <span className="font-roboto-flex text-sm md:text-base">Saved Posts</span>
                  </Link>

                  <Link
                    className={`flex gap-2 md:gap-3 items-center text-sm font-medium py-2 md:py-2.5 px-2 md:px-3 rounded-lg transition-all duration-200 ${
                      currentLocation === "/skills-feed/private"
                        ? "text-primary-700 bg-primary-50"
                        : "text-gray-700 hover:text-primary-800 hover:bg-gray-50"
                    }`}
                    to="/skills-feed/private"
                  >
                    <FaLock className={`text-base md:text-lg transition-colors ${
                      currentLocation === "/skills-feed/private"
                        ? "text-primary-700"
                        : "text-gray-500"
                    }`} />
                    <span className="font-roboto-flex text-sm md:text-base">My Private Posts</span>
                  </Link>

                </div>
              </div>
            )}

            {/* Communities Card */}
            {!isSkillsCirclePage && !isMySkillsCommunitiesPage && communityLinks && communityLinks.length > 0 && (
              <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex gap-2 md:gap-3 items-center">
                    <FaUsersCog className="text-base md:text-lg text-gray-500" />
                    <span className="text-sm md:text-base font-semibold text-gray-800 font-roboto-flex">Post in My Communities</span>
                  </div>
                </div>
                
                <div className="space-y-0">
                  {(isCommunitiesExpanded ? communityLinks : communityLinks.slice(0, 5)).map((communityLink) => {
                    // Decode URL-encoded characters for proper comparison
                    const decodedCurrentLocation = decodeURIComponent(currentLocation);
                    const isActive = decodedCurrentLocation === communityLink.href || 
                      decodedCurrentLocation.startsWith(communityLink.href + '/') ||
                      decodedCurrentLocation === `/skills-feed/${communityLink.label}` ||
                      decodedCurrentLocation.startsWith(`/skills-feed/${communityLink.label}/`);
                    
                    return (
                      <Link
                        key={communityLink.href}
                        className={`block text-sm font-medium py-2 pl-8 pr-3 rounded-lg transition-all duration-200 font-roboto-flex ${
                          isActive
                            ? "text-primary-700 bg-primary-50"
                            : "text-gray-700 hover:text-primary-800 hover:bg-gray-50"
                        }`}
                        to={communityLink.href}
                      >
                        {communityLink.label}
                      </Link>
                    );
                  })}
                </div>

                {/* See All Button - Only show if more than 5 communities */}
                {joinedCommunities.length > 5 && (
                  <div className="mt-3 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => setIsCommunitiesExpanded(!isCommunitiesExpanded)}
                      className="flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800 transition-colors font-roboto-flex whitespace-nowrap w-full justify-center"
                    >
                      {isCommunitiesExpanded ? 'Show Less' : 'See All'}
                      {!isCommunitiesExpanded && (
                        <span className="text-white text-xs bg-primary-700 w-4 h-4 rounded-full flex justify-center items-center font-medium font-roboto-flex">
                          {joinedCommunities.length - 5}
                        </span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

        {/* My Skills Communities */}
        {isMySkillsCommunitiesPage && (
          <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm">
            <div className="flex flex-col space-y-0">
              {/* My Skills Communities */}
              <Link
                className={`flex gap-2 md:gap-3 items-center text-sm font-medium py-2 md:py-2.5 px-2 md:px-3 rounded-lg transition-all duration-200 ${
                  currentLocation === "/skills-communities/my-communities"
                    ? "text-primary-700 bg-primary-50"
                    : "text-gray-700 hover:text-primary-800 hover:bg-gray-50"
                }`}
                to="/skills-communities/my-communities"
              >
                <span className="relative inline-flex items-center justify-center h-5 w-5 shrink-0">
                  <FaBook className={`w-4 h-4 transition-colors ${
                    currentLocation === "/skills-communities/my-communities"
                      ? "text-primary-700"
                      : "text-gray-500"
                  }`} />
                  <FaUsers className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-white ${
                    currentLocation === "/skills-communities/my-communities"
                      ? "text-primary-700"
                      : "text-gray-400"
                  }`} />
                </span>
                <span className="font-roboto-flex text-sm md:text-base">My Skills Communities</span>
              </Link>

              {/* Suggested Skills Communities */}
              <Link
                className={`flex gap-2 md:gap-3 items-center text-sm font-medium py-2 md:py-2.5 px-2 md:px-3 rounded-lg transition-all duration-200 ${
                  currentLocation === "/skills-communities/suggested"
                    ? "text-primary-700 bg-primary-50"
                    : "text-gray-700 hover:text-primary-800 hover:bg-gray-50"
                }`}
                to="/skills-communities/suggested"
              >
                <span className="relative inline-flex items-center justify-center h-5 w-5 shrink-0">
                  <FaCompass className={`w-5 h-5 transition-colors ${
                    currentLocation === "/skills-communities/suggested"
                      ? "text-primary-700"
                      : "text-gray-500"
                  }`} />
                  <FaUsers className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-white ${
                    currentLocation === "/skills-communities/suggested"
                      ? "text-primary-700"
                      : "text-gray-400"
                  }`} />
                </span>
                <span className="font-roboto-flex text-sm md:text-base">Suggested Skills Communities</span>
              </Link>
            </div>
          </div>
        )}

        {/* Skills Circle - Network Items */}
        {isSkillsCirclePage && (() => {
          const isMySkillsCirclePage = currentLocation === "/skills-circle/followers" || currentLocation === "/skills-circle/following";
          const isRequestsPage = currentLocation === "/skills-circle/received-profile-requests" || 
                                 currentLocation === "/skills-circle/sent-profile-requests" ||
                                 currentLocation === "/skills-circle/received-follow-requests" ||
                                 currentLocation === "/skills-circle/sent-follow-requests";
          
          return (
            <div className="bg-white rounded-lg border p-3 md:p-4 shadow-sm">
              <div className="flex flex-col gap-2 md:gap-3">
                {/* Requests - Main Item */}
                <div>
                  {!isRequestsPage ? (
                    <Link
                      className="flex items-center justify-between text-sm md:text-base font-semibold hover:text-primary font-roboto-flex p-2 rounded-lg transition-colors text-gray-700 hover:text-primary-800 hover:bg-gray-50"
                      to="/skills-circle/received-follow-requests"
                    >
                      <div className="flex items-center gap-2 md:gap-3">
                        <FaEnvelope className="text-base md:text-lg transition-colors text-gray-600 hover:text-primary" />
                        <span className="font-roboto-flex">Requests</span>
                      </div>
                    </Link>
                  ) : (
                    <div className={`flex items-center justify-between text-sm md:text-base font-semibold font-roboto-flex p-2 rounded-lg transition-colors text-primary-700 bg-primary-50`}>
                      <div className="flex items-center gap-2 md:gap-3">
                        <FaEnvelope className="text-base md:text-lg transition-colors text-primary-700" />
                        <span className="font-roboto-flex">Requests</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* My Skills Circle - Main Item */}
                <div>
                  {!isMySkillsCirclePage ? (
                    <Link
                      className="flex items-center justify-between text-sm md:text-base font-semibold hover:text-primary font-roboto-flex p-2 rounded-lg transition-colors text-gray-700 hover:text-primary-800 hover:bg-gray-50"
                      to="/skills-circle/followers"
                    >
                      <div className="flex items-center gap-2 md:gap-3">
                        <FaUsers className="text-base md:text-lg transition-colors text-gray-600 hover:text-primary" />
                        <span className="font-roboto-flex">My Skills Circle</span>
                      </div>
                    </Link>
                  ) : (
                    <div className={`flex items-center justify-between text-sm md:text-base font-semibold font-roboto-flex p-2 rounded-lg transition-colors text-primary-700 bg-primary-50`}>
                      <div className="flex items-center gap-2 md:gap-3">
                        <FaUsers className="text-base md:text-lg transition-colors text-primary-700" />
                        <span className="font-roboto-flex">My Skills Circle</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      </div>
      )}

      {/* Main Content */}
      <div className={`flex-1 w-full ${isPostDetailPage ? 'lg:ml-0 lg:flex-1 lg:min-w-0' : 'lg:flex-1 lg:min-w-0'} order-1 lg:order-2 min-w-0 ${isProfilePage ? '' : ''}`}>
        {children}
      </div>

      {/* Right Cards Sidebar - Hidden on mobile */}
      <div className="hidden md:block lg:w-60 space-y-2 md:space-y-3 order-3 lg:-mr-6">
        {/* Trending Topics Card - For Skills Feed, Following, Saved, Private Posts, and Post Detail Pages */}
        {(currentLocation === '/skills-feed' || 
          currentLocation === '/' || 
          currentLocation === '/skills-feed/following' || 
          currentLocation === '/skills-feed/saved' || 
          currentLocation === '/skills-feed/private' ||
          isPostDetailPage) && (
          <div className="bg-white rounded-lg border p-4 shadow-sm">
            <h5 className="mb-4 text-sm font-semibold font-roboto-flex">Trending Topics</h5>
            <div className="text-center text-gray-500 font-roboto-flex py-8">
              <div className="text-4xl mb-3">📈</div>
              <p className="text-sm">Coming Soon</p>
            </div>
          </div>
        )}

        {/* Coming Soon Card for My Skills Communities */}
        {isMySkillsCommunitiesPage && (
          <div className="bg-white rounded-lg border p-4 shadow-sm">
            <h5 className="mb-4 text-sm font-semibold font-roboto-flex">Content Section</h5>
            <div className="text-center text-gray-500 font-roboto-flex py-8">
              <div className="text-4xl mb-3">🚀</div>
              <p className="text-sm">Coming Soon</p>
            </div>
          </div>
        )}

        {/* Suggested Communities Card - For all other pages except Skills Feed, Following, Saved, Private Posts, and Post Detail Pages */}
        {currentLocation !== "/communities" && 
         !isSkillsCirclePage && 
         !isMySkillsCommunitiesPage && 
         currentLocation !== '/skills-feed' && 
         currentLocation !== '/' && 
         currentLocation !== '/skills-feed/following' && 
         currentLocation !== '/skills-feed/saved' && 
         currentLocation !== '/skills-feed/private' && 
         !isPostDetailPage &&
         !isPrivateProfileAccessDenied && (
          <div className="bg-white rounded-lg border p-3 md:p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-semibold text-xs md:text-sm font-roboto-flex">Available Communities</h5>
              {remainingCount > 0 && (
                <Link
                  className="flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800 transition-colors font-roboto-flex whitespace-nowrap"
                  to="/communities"
                >
                  See all
                  <span className="text-white text-xs bg-primary-700 w-4 h-4 rounded-full flex justify-center items-center font-medium font-roboto-flex">
                    {remainingCount}
                  </span>
                </Link>
              )}
            </div>

            {notJoinedCommunitiesFetched && visibleNotJoinedCommunities.length === 0 && (
              <div className="text-center text-gray-400 font-roboto-flex py-2 md:py-4 text-xs md:text-sm">
                No communities to join. Check back later
              </div>
            )}
            
            <div className="space-y-1">
              {visibleNotJoinedCommunities?.map((community) => (
                <Link
                  key={community._id}
                  to={`/skills-feed/${community.name}`}
                  className="flex items-center py-2 pl-1 pr-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <img
                    src={community.banner || placeholder}
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full mr-1 md:mr-2 object-cover flex-shrink-0"
                    alt="community"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="group relative">
                      <p className="text-xs md:text-sm font-medium font-roboto-flex break-words">{community.name}</p>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 font-roboto-flex">
                        {community.name}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 flex items-center gap-1 font-roboto-flex">
                      <IoMdPeople className="text-xs" />
                      {community.members.length}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Popular Users Card - For all pages except Skills Feed, Following, Saved, Private Posts, and Post Detail Pages */}
        {currentLocation !== '/skills-feed' && 
         currentLocation !== '/' && 
         currentLocation !== '/skills-feed/following' && 
         currentLocation !== '/skills-feed/saved' && 
         currentLocation !== '/skills-feed/private' && 
         !isPostDetailPage && 
         !isMySkillsCommunitiesPage &&
         !isPrivateProfileAccessDenied && (
          <div className="bg-white rounded-lg border p-4 shadow-sm">
            <h5 className="mb-2 text-sm font-semibold font-roboto-flex">Popular Users to Follow</h5>

            {/* On Skills Circle / My Communities / Profile: only show users list after fetch for this route (avoids showing then hiding stale users) */}
            {(() => {
              const isMatchingSkillsPage = isSkillsCirclePage || isMyCommunitiesPage || isProfilePage;
              const usersAreForCurrentRoute = !isMatchingSkillsPage || (lastFetchedPathnameRef.current === currentLocation);
              return (
                <>
                  {usersAreForCurrentRoute && publicUsersFetched && recommendedUsers?.length === 0 && (
                    <div className="text-center text-gray-400 font-roboto-flex py-2 md:py-3 text-xs md:text-sm">
                      {isMatchingSkillsPage
                        ? "No users with matching skills. Check back later"
                        : "No users to follow. Check back later"}
                    </div>
                  )}
                  {!usersAreForCurrentRoute && isMatchingSkillsPage && (
                    <div className="text-center text-gray-400 font-roboto-flex py-2 md:py-3 text-xs md:text-sm">
                      Loading…
                    </div>
                  )}
                  <div className="space-y-1">
                    {usersAreForCurrentRoute && recommendedUsers?.length > 0 &&
                recommendedUsers.map((user) => {
                  const targetHref = user.profileUrl
                    ? `/profile/${user.profileUrl}`
                    : user.username
                    ? `/profile/${user.username}`
                    : `/profile/${user._id}`;
                  return (
                    <Link
                      key={user._id}
                      to={targetHref}
                      className="flex items-start gap-1 py-2 pl-1 pr-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <img
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0 object-cover mt-0.5"
                        src={user.avatar}
                        alt={user.name}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="group relative">
                          <p className="font-medium text-xs md:text-sm font-roboto-flex break-words">{user.name}</p>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 font-roboto-flex">
                            {user.name}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 flex items-center gap-1 font-roboto-flex">
                          <IoMdPeople className="text-xs" />
                          {user.followerCount} follower{user.followerCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </Link>
                  );
                })}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(ContentWithCards);